import { fetchWithTimeout } from './deco-http.js';
import { CartAddEvent } from '@theme/events';
import { addSystemSelectionItems } from '@theme/system-selection';

const STORAGE_KEY = 'decorental.packAssistant.v1';
const VISITOR_STORAGE_KEY = 'decorental.packAssistant.visitorId';
const PRODUCT_IMAGE_CACHE_STORAGE_KEY = 'deco_account_product_images';
const PRODUCT_IMAGE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const PACK_ORDER = ['economico', 'recomendado', 'premium'];
const PACK_LABELS = {
  economico: 'Económico',
  recomendado: 'Recomendado',
  premium: 'Premium',
};
const ASSISTANT_TYPING_MIN_MS = 850;
const ASSISTANT_GENERATION_MIN_MS = 2200;
const productImageRequests = new Map();

function trackAssistantEvent(eventName, params = {}) {
  globalThis.DecoAnalytics?.track?.(eventName, {
    source: 'ASISTENTE',
    ...params,
  });
}

document.querySelectorAll('[data-deco-pack-assistant]').forEach((root) => {
  initAssistant(root);
});

function initAssistant(root) {
  const headerEl = root.querySelector('.deco-assistant__header');
  const surfaceEl = root.querySelector('.deco-assistant__surface');
  const chatLogEl = root.querySelector('[data-assistant-chat-log]');
  const inputEl = root.querySelector('[data-assistant-input]');
  const sendButton = root.querySelector('[data-assistant-send]');
  const resetButton = root.querySelector('[data-assistant-reset]');
  const statusEl = root.querySelector('[data-assistant-status]');
  const loadingEl = root.querySelector('[data-assistant-loading]');
  const resultsEl = root.querySelector('[data-assistant-results]');
  const products = readAssistantProducts(root);
  let packModal = null;
  let dockVisibilityObserver = null;
  let thinkingStartedAt = 0;
  let restoreComposerFocus = false;
  let assistantOpenTracked = false;

  if (!chatLogEl || !inputEl || !sendButton || !resetButton || !statusEl || !resultsEl) {
    return;
  }

  const saved = readSavedState();
  let state = {
    conversationState: saved.conversationState && typeof saved.conversationState === 'object' ? saved.conversationState : {},
    messages: Array.isArray(saved.messages) ? saved.messages : [],
    requestId: saved.requestId || '',
    recommendations: Array.isArray(saved.recommendations) ? saved.recommendations : [],
    budgetNotice: normalizeBudgetNotice(saved.budgetNotice),
    thinkingLabel: null,
    isSubmitting: false,
  };

  sendButton.addEventListener('click', () => {
    submitUserMessage();
  });

  inputEl.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    submitUserMessage();
  });

  inputEl.addEventListener('input', () => {
    sendButton.disabled = state.isSubmitting || !inputEl.value.trim();
    updateDockVisibilityFallback();
  });

  inputEl.addEventListener('focus', () => {
    if (!assistantOpenTracked) {
      assistantOpenTracked = true;
      trackAssistantEvent('assistant_opened');
    }
    updateKeyboardOffset();
    window.setTimeout(updateKeyboardOffset, 80);
    window.setTimeout(updateKeyboardOffset, 240);
  });
  inputEl.addEventListener('blur', () => {
    window.setTimeout(updateKeyboardOffset, 120);
  });
  resetButton.addEventListener('click', resetAssistant);
  window.visualViewport?.addEventListener('resize', updateKeyboardOffset);
  window.visualViewport?.addEventListener('scroll', updateKeyboardOffset);
  window.addEventListener('orientationchange', () => {
    window.setTimeout(updateKeyboardOffset, 250);
  });
  updateKeyboardOffset();

  function resetAssistant() {
    state = {
      conversationState: {},
      messages: [],
      requestId: '',
      recommendations: [],
      budgetNotice: null,
      thinkingLabel: null,
      isSubmitting: false,
    };
    saveState(state);
    resultsEl.hidden = true;
    resultsEl.innerHTML = '';
    statusEl.textContent = '';
    inputEl.value = '';
    renderConversation();
    requestAssistantTurn('');
  }

  renderConversation();
  if (state.recommendations.length === PACK_ORDER.length) {
    ensurePackSummaryMessage();
    statusEl.textContent = 'Estas opciones quedaron guardadas en este navegador.';
    setInputDisabled(false);
  } else if (!state.messages.length) {
    requestAssistantTurn('');
  } else {
    setInputDisabled(false);
  }

  async function submitUserMessage() {
    if (state.isSubmitting) return;

    const message = inputEl.value.trim();
    if (!message) return;

    const shouldShowGenerationLoading = state.conversationState?.current_slot === 'extra_details'
      || state.conversationState?.budget_adjustment_pending === true
      || state.conversationState?.complete === true;
    restoreComposerFocus = true;
    trackAssistantEvent('assistant_message_sent', {
      metadata: { assistant_status: state.conversationState?.current_slot || 'start' },
    });
    inputEl.value = '';
    appendMessage('user', message);
    await requestAssistantTurn(message, { showGenerationLoading: shouldShowGenerationLoading });
  }

  async function requestAssistantTurn(message, options = {}) {
    state.isSubmitting = true;
    setInputDisabled(true);
    statusEl.textContent = '';
    const thinkingMinMs = options.showGenerationLoading ? ASSISTANT_GENERATION_MIN_MS : ASSISTANT_TYPING_MIN_MS;
    showThinking(options.showGenerationLoading ? 'Estoy revisando disponibilidad, calidades y valores.' : '');

    try {
      const result = await fetchAssistantChat(root, message, state.conversationState);
      state.conversationState = result?.state || {};
      state.requestId = result?.request_id || state.requestId || '';
      await waitForThinking(thinkingMinMs);
      hideThinking();

      if (result?.status === 'completed' && Array.isArray(result?.packs) && result.packs.length) {
        clearPackResultMessages();
        if (result?.assistant_message) {
          appendMessage('assistant', result.assistant_message, { save: false });
        }
        const normalizedPacks = normalizePacks(result, products);
        state.recommendations = normalizedPacks;
        state.budgetNotice = normalizeBudgetNotice(result?.budget_notice);
        trackAssistantEvent('assistant_recommendations_generated', {
          metadata: {
            assistant_status: 'completed',
            pack_count: normalizedPacks.length,
            request_id: state.requestId,
          },
        });
        appendPacksSummary(normalizedPacks, { save: false });
        statusEl.textContent = 'Listo. Estas opciones quedan guardadas en este navegador.';
        setInputDisabled(false);
      } else if (result?.status === 'completed') {
        if (result?.assistant_message) {
          appendMessage('assistant', result.assistant_message, { save: false });
        }
        state.budgetNotice = null;
        setInputDisabled(false);
      } else {
        if (result?.assistant_message) {
          appendMessage('assistant', result.assistant_message, { save: false });
        }
        setInputDisabled(false);
        statusEl.textContent = '';
      }
      saveState(state);
    } catch (error) {
      hideThinking();
      if (!state.messages.length && !state.recommendations.length) {
        state.requestId = '';
        renderAssistantUnavailable();
      } else {
        appendMessage('assistant', 'No pude procesar ese cambio. Probá decírmelo de nuevo, por ejemplo: "agregá un espejo" o "sacá la mesa ratona".', { save: false });
        setInputDisabled(false);
      }
      statusEl.textContent = '';
      trackAssistantEvent('assistant_error', {
        metadata: { error_code: 'request_failed' },
      });
      console.warn('[deco-assistant] No se pudo continuar la conversación del agente.', error);
    } finally {
      state.isSubmitting = false;
      if (!state.isSubmitting) {
        setInputDisabled(false);
      }
      saveState(state);
    }
  }

  function renderConversation() {
    if (headerEl) headerEl.hidden = false;
    if (surfaceEl) surfaceEl.hidden = false;
    if (loadingEl) {
      loadingEl.hidden = true;
      loadingEl.classList.remove('deco-assistant__loading--visible', 'deco-assistant__loading--leaving');
    }
    resultsEl.hidden = true;
    chatLogEl.innerHTML = `${state.messages.map(renderChatMessage).join('')}${renderThinkingIndicator()}`;
    bindPackSummaryButtons();
    hydratePackItemImages(chatLogEl);
    observeDockVisibilityTarget();
    scrollConversationToLatest();
  }

  function scrollConversationToLatest() {
    chatLogEl.scrollTop = chatLogEl.scrollHeight;
    const latestMessage = chatLogEl.lastElementChild;
    if (!latestMessage) return;
    window.requestAnimationFrame(() => {
      latestMessage.scrollIntoView({ block: 'end', behavior: 'smooth' });
      window.setTimeout(() => {
        latestMessage.scrollIntoView({ block: 'end', behavior: 'smooth' });
      }, 120);
    });
  }

  function appendMessage(role, text, options = {}) {
    const cleanText = String(text || '').trim();
    if (!cleanText) return;
    state.messages.push({ role, text: cleanText, budgetNotice: options.budgetNotice === true });
    renderConversation();
    if (options.save !== false) {
      saveState(state);
    }
  }

  function renderChatMessage(message) {
    const role = message?.role === 'user' ? 'user' : 'assistant';
    if (role === 'assistant' && message?.type === 'packs') {
      return renderPackSummaryMessage(message?.packs || []);
    }
    return `
      <div class="deco-assistant__message deco-assistant__message--${role}">
        <div class="deco-assistant__bubble">${escapeHtml(message?.text || '')}</div>
      </div>
    `;
  }

  function showThinking(label = '') {
    state.thinkingLabel = String(label || '');
    thinkingStartedAt = Date.now();
    renderConversation();
  }

  function hideThinking() {
    if (state.thinkingLabel === null) return;
    state.thinkingLabel = null;
    renderConversation();
  }

  function waitForThinking(minMs) {
    const elapsed = Date.now() - thinkingStartedAt;
    const remaining = Math.max(0, Number(minMs || 0) - elapsed);
    if (!remaining) return Promise.resolve();
    return new Promise((resolve) => {
      window.setTimeout(resolve, remaining);
    });
  }

  function renderThinkingIndicator() {
    if (state.thinkingLabel === null) return '';
    const label = String(state.thinkingLabel || '').trim();
    return `
      <div class="deco-assistant__message deco-assistant__message--assistant deco-assistant__message--thinking">
        <div class="deco-assistant__bubble deco-assistant__bubble--thinking">
          <span class="deco-assistant__typing" aria-label="${escapeHtml(label || 'El asistente está escribiendo')}">
            <span></span><span></span><span></span>
          </span>
          ${label ? `<span class="deco-assistant__thinking-label">${escapeHtml(label)}</span>` : ''}
        </div>
      </div>
    `;
  }

  function appendPacksSummary(packs, options = {}) {
    state.messages.push({
      role: 'assistant',
      type: 'packs',
      text: 'packs',
      packs: summarizePacksForMessage(packs),
    });
    renderConversation();
    if (options.save !== false) {
      saveState(state);
    }
  }

  function clearPackResultMessages() {
    state.messages = state.messages.filter((message) => message?.type !== 'packs' && message?.budgetNotice !== true);
  }

  function observeDockVisibilityTarget() {
    if (!window.matchMedia('(max-width: 820px)').matches) {
      root.classList.add('deco-assistant--dock-visible');
      return;
    }

    root.classList.remove('deco-assistant--dock-visible');
    dockVisibilityObserver?.disconnect();
    dockVisibilityObserver = null;

    const messages = Array.from(chatLogEl.querySelectorAll('.deco-assistant__message'));
    const lastAssistantMessage = messages.reverse().find((message) => message.classList.contains('deco-assistant__message--assistant'));
    const target = lastAssistantMessage || chatLogEl.lastElementChild;
    if (!(target instanceof Element)) {
      updateDockVisibilityFallback();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      updateDockVisibilityFallback();
      return;
    }

    dockVisibilityObserver = new IntersectionObserver((entries) => {
      const isVisible = entries.some((entry) => entry.isIntersecting);
      root.classList.toggle('deco-assistant--dock-visible', isVisible);
    }, {
      root: null,
      threshold: 0.75,
      rootMargin: '0px 0px -118px 0px',
    });
    dockVisibilityObserver.observe(target);
  }

  function updateDockVisibilityFallback() {
    if (!window.matchMedia('(max-width: 820px)').matches) {
      root.classList.add('deco-assistant--dock-visible');
      return;
    }

    const latestMessage = chatLogEl.lastElementChild;
    if (!(latestMessage instanceof HTMLElement)) {
      root.classList.toggle('deco-assistant--dock-visible', Boolean(inputEl.value.trim()));
      return;
    }

    const rect = latestMessage.getBoundingClientRect();
    root.classList.toggle('deco-assistant--dock-visible', rect.bottom <= window.innerHeight - 118);
  }

  function updateKeyboardOffset() {
    const viewport = window.visualViewport;
    if (!viewport || !window.matchMedia('(max-width: 820px)').matches) {
      root.style.setProperty('--deco-assistant-keyboard-offset', '0px');
      return;
    }

    const keyboardOffset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
    root.style.setProperty('--deco-assistant-keyboard-offset', `${Math.round(keyboardOffset)}px`);
  }

  function ensurePackSummaryMessage() {
    if (!state.recommendations.length) return;
    const hasSummary = state.messages.some((message) => message?.type === 'packs');
    if (!hasSummary) {
      state.messages.push({
        role: 'assistant',
        type: 'packs',
        text: 'packs',
        packs: summarizePacksForMessage(state.recommendations),
      });
      saveState(state);
    }
    renderConversation();
  }

  function renderPackSummaryMessage(packs) {
    const source = Array.isArray(packs) && packs.length ? packs : state.recommendations;
    return `
      <div class="deco-assistant__message deco-assistant__message--assistant">
        <div class="deco-assistant__bubble deco-assistant__bubble--packs">
          <div class="deco-assistant__pack-strip">
            ${source.map(renderPackSummaryButton).join('')}
          </div>
        </div>
      </div>
    `;
  }

  function renderPackSummaryButton(pack) {
    const packType = normalizePackType(pack?.type);
    const price = pack?.valor_final ? `${formatCurrency(pack.valor_final, pack.currency)}/mes` : 'Ver detalle';
    return `
      <button class="deco-assistant__pack-chip" type="button" data-assistant-pack-open="${escapeHtml(packType)}">
        <span class="deco-assistant__pack-chip-label">${escapeHtml(PACK_LABELS[packType] || pack?.label || 'Pack')}</span>
        <span class="deco-assistant__pack-chip-title">${escapeHtml(pack?.title || `${PACK_LABELS[packType] || 'Pack'} Deco`)}</span>
        <span class="deco-assistant__pack-chip-price">${escapeHtml(price)}</span>
        ${renderPackComparisonSummary(pack?.purchase_comparison)}
      </button>
    `;
  }

  function bindPackSummaryButtons() {
    chatLogEl.querySelectorAll('[data-assistant-pack-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const packType = normalizePackType(button.getAttribute('data-assistant-pack-open'));
        const pack = state.recommendations.find((candidate) => normalizePackType(candidate.type) === packType);
        if (pack) openPackModal(pack);
      });
    });
  }

  function setInputDisabled(disabled) {
    inputEl.disabled = disabled;
    sendButton.disabled = disabled || !inputEl.value.trim();
    if (!disabled && restoreComposerFocus) {
      restoreComposerFocus = false;
      if (isMobileAssistantViewport()) {
        scrollConversationToLatest();
        return;
      }
      focusComposer();
    }
  }

  function isMobileAssistantViewport() {
    return window.matchMedia('(max-width: 820px)').matches;
  }

  function focusComposer() {
    window.requestAnimationFrame(() => {
      if (inputEl.disabled || document.activeElement === inputEl) return;
      try {
        inputEl.focus({ preventScroll: true });
      } catch (_error) {
        inputEl.focus();
      }
    });
  }

  function renderPacks(packs) {
    resultsEl.innerHTML = '';
    if (headerEl) headerEl.hidden = true;
    if (surfaceEl) surfaceEl.hidden = true;
    if (loadingEl) loadingEl.hidden = true;
    resultsEl.hidden = false;

    const actions = document.createElement('div');
    actions.className = 'deco-assistant__results-actions';
    const startOverButton = document.createElement('button');
    startOverButton.type = 'button';
    startOverButton.className = 'deco-assistant__button deco-assistant__button--ghost deco-assistant__reset';
    startOverButton.textContent = 'Empezar de nuevo';
    startOverButton.addEventListener('click', resetAssistant);
    actions.append(startOverButton);
    resultsEl.append(actions);

    packs.forEach((pack) => {
      const card = document.createElement('article');
      const packType = normalizePackType(pack.type);
      card.className = `deco-assistant__pack${packType === 'recomendado' ? ' deco-assistant__pack--recommended' : ''}`;

      const items = normalizePackItems(pack.items, products);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'deco-assistant__button deco-assistant__pack-action';
      button.textContent = 'Elegir este pack';
      button.disabled = items.length === 0;
      button.addEventListener('click', async () => {
        button.disabled = true;
        button.textContent = 'Agregando...';
        statusEl.textContent = '';

        try {
          const cartState = await addPackToCart(root, items);
          trackAssistantEvent('assistant_pack_added', {
            selection_id: cartState?.selection_id || null,
            metadata: {
              pack_type: packType,
              item_count: items.reduce((total, item) => total + Number(item.qty || 0), 0),
            },
          });
          button.textContent = 'Pack agregado';
          statusEl.textContent = 'Agregamos el pack a Mi selección.';
        } catch (error) {
          button.disabled = false;
          button.textContent = 'Elegir este pack';
          statusEl.textContent = 'No pudimos agregar el pack a Mi selección. Probá nuevamente.';
          console.warn('[deco-assistant] No se pudo agregar el pack a Mi selección.', error);
        }
      });

      const body = document.createElement('div');
      body.className = 'deco-assistant__pack-body';
      body.innerHTML = `
        <div class="deco-assistant__pack-head">
          <p class="deco-assistant__pack-kicker">${escapeHtml(PACK_LABELS[packType] || pack.label || 'Pack')}</p>
          <h3 class="deco-assistant__pack-title">${escapeHtml(pack.title || `${PACK_LABELS[packType] || 'Pack'} Deco`)}</h3>
          ${pack.valor_final ? `<p class="deco-assistant__pack-price">${escapeHtml(formatCurrency(pack.valor_final, pack.currency))}/mes</p>` : ''}
        </div>
        <ul class="deco-assistant__pack-items">
          ${items.length ? items.map(renderPackItem).join('') : '<li class="deco-assistant__pack-item">Sin productos disponibles para esta opción.</li>'}
        </ul>
      `;
      body.append(button);
      card.append(body);
      resultsEl.append(card);
    });

    hydratePackItemImages(resultsEl);
  }

  function openPackModal(pack) {
    const modal = ensurePackModal();
    const content = modal.querySelector('[data-assistant-pack-modal-content]');
    if (!content) return;

    const packType = normalizePackType(pack.type);
    const items = normalizePackItems(pack.items, products);
    trackAssistantEvent('assistant_pack_viewed', {
      metadata: { pack_type: packType },
    });
    const price = pack.valor_final ? `${formatCurrency(pack.valor_final, pack.currency)}/mes` : '';
    content.innerHTML = `
      <div class="deco-assistant__pack-modal-head">
        <p class="deco-assistant__pack-kicker">${escapeHtml(PACK_LABELS[packType] || 'Pack')}</p>
        <h3 class="deco-assistant__pack-title">${escapeHtml(pack.title || `${PACK_LABELS[packType] || 'Pack'} Deco`)}</h3>
        ${price ? `<p class="deco-assistant__pack-price">${escapeHtml(price)}</p>` : ''}
      </div>
      <ul class="deco-assistant__pack-items deco-assistant__pack-items--modal">
        ${items.length ? items.map(renderPackItem).join('') : '<li class="deco-assistant__pack-item">Sin productos disponibles para esta opción.</li>'}
      </ul>
      <button class="deco-assistant__button deco-assistant__pack-action" type="button" data-assistant-pack-add>
        Elegir este pack
      </button>
    `;

    const addButton = content.querySelector('[data-assistant-pack-add]');
    bindPackProductPreviewButtons(content);
    addButton?.addEventListener('click', async () => {
      addButton.disabled = true;
      addButton.textContent = 'Agregando...';
      statusEl.textContent = '';

      try {
        const cartState = await addPackToCart(root, items);
        trackAssistantEvent('assistant_pack_added', {
          selection_id: cartState?.selection_id || null,
          metadata: {
            pack_type: packType,
            item_count: items.reduce((total, item) => total + Number(item.qty || 0), 0),
          },
        });
        addButton.textContent = 'Pack agregado';
        statusEl.textContent = 'Agregamos el pack a Mi selección.';
        closePackModal();
      } catch (error) {
        addButton.disabled = false;
        addButton.textContent = 'Elegir este pack';
        statusEl.textContent = 'No pudimos agregar el pack a Mi selección. Probá nuevamente.';
        console.warn('[deco-assistant] No se pudo agregar el pack a Mi selección.', error);
      }
    });

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('deco-assistant-pack-modal-open');
    hydratePackItemImages(modal);
  }

  function bindPackProductPreviewButtons(scope) {
    scope.querySelectorAll('[data-assistant-product-preview]').forEach((button) => {
      button.addEventListener('click', async () => {
        if (!(button instanceof HTMLElement)) return;

        const productUrl = button.dataset.productUrl || await resolvePackItemProductUrl({
          sku: button.dataset.sku || '',
          title: button.dataset.name || '',
        });
        if (!productUrl) return;

        const openEvent = new CustomEvent('product-preview-modal:open', {
          bubbles: true,
          cancelable: true,
          detail: {
            url: productUrl,
            mode: 'readonly',
          },
        });
        document.dispatchEvent(openEvent);
      });
    });
  }

  function ensurePackModal() {
    if (packModal) return packModal;

    packModal = document.createElement('div');
    packModal.className = 'deco-assistant__pack-modal';
    packModal.hidden = true;
    packModal.setAttribute('aria-hidden', 'true');
    packModal.innerHTML = `
      <div class="deco-assistant__pack-modal-backdrop" data-assistant-pack-modal-close></div>
      <section class="deco-assistant__pack-modal-dialog" role="dialog" aria-modal="true" aria-label="Detalle del pack">
        <button class="deco-assistant__pack-modal-close" type="button" data-assistant-pack-modal-close aria-label="Cerrar">Cerrar</button>
        <div class="deco-assistant__pack-modal-content" data-assistant-pack-modal-content></div>
      </section>
    `;
    packModal.addEventListener('click', (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-assistant-pack-modal-close]')) {
        closePackModal();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && packModal && !packModal.hidden) {
        closePackModal();
      }
    });
    root.append(packModal);
    return packModal;
  }

  function closePackModal() {
    if (!packModal) return;
    packModal.hidden = true;
    packModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('deco-assistant-pack-modal-open');
  }

  function renderAssistantUnavailable() {
    resultsEl.innerHTML = `
      <div class="deco-assistant__unavailable" role="status" aria-live="polite">
        <p>No es posible usar el asistente en estos momentos. Podés armar un pack en <a href="/collections/productos">"Arma tu espacio"</a>.</p>
      </div>
    `;
    if (headerEl) headerEl.hidden = true;
    if (surfaceEl) surfaceEl.hidden = true;
    if (loadingEl) loadingEl.hidden = true;
    resultsEl.hidden = false;
  }
}

async function fetchAssistantChat(root, message, conversationState) {
  const endpoint = assistantChatEndpoint(root);
  if (!endpoint) {
    throw new Error('Endpoint del agente no configurado.');
  }

  const response = await fetchWithTimeout(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      message,
      state: conversationState || {},
      source: 'shopify_chat_assistant',
      visitor_id: getVisitorId(),
    }),
  }, 105000);

  if (!response.ok) {
    throw new Error(response.status === 429 || response.status === 503 ? 'Hay muchas consultas en este momento. Intentá nuevamente en un minuto.' : 'No pudimos completar la consulta. Intentá nuevamente.');
  }

  const payload = await response.json();
  if (payload?.ok === false || payload?.error) {
    throw new Error(payload?.error || 'Respuesta inválida del agente.');
  }
  return payload;
}

function assistantChatEndpoint(root) {
  const endpoint = String(root.dataset.agentEndpoint || '').trim();
  if (!endpoint) return '';
  return endpoint.replace(/\/recommendations\/?$/, '/chat');
}

function normalizePacks(payload, products) {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.packs)
      ? payload.packs
      : Array.isArray(payload?.recommendations)
        ? payload.recommendations
        : Array.isArray(payload?.data?.packs)
          ? payload.data.packs
          : [];

  const byType = new Map();

  source.forEach((pack, index) => {
    const type = normalizePackType(pack?.type || pack?.tier || pack?.name || PACK_ORDER[index]);
    byType.set(type, {
      type,
      label: pack?.label || pack?.name || PACK_LABELS[type],
      title: pack?.title || pack?.name || `${PACK_LABELS[type] || 'Pack'} Deco`,
      reason: pack?.reason || pack?.description || pack?.summary || '',
      valor_final: pack?.valor_final ?? pack?.quote?.valor_final ?? null,
      currency: pack?.currency || pack?.quote?.currency || 'ARS',
      puntaje_total: pack?.puntaje_total ?? pack?.score ?? null,
      purchase_comparison: normalizePurchaseComparison(pack?.purchase_comparison),
      items: pack?.items || pack?.products || pack?.line_items || [],
    });
  });

  const packs = PACK_ORDER
    .map((type) => {
      const pack = byType.get(type);
      if (!pack) return null;

      const matchedItems = normalizePackItems(pack.items, products);
      return {
        ...pack,
        items: matchedItems,
      };
    })
    .filter(Boolean);

  if (packs.length !== PACK_ORDER.length || packs.some((pack) => !pack.items.length)) {
    throw new Error('Respuesta incompleta del agente.');
  }

  return packs;
}

function summarizePacksForMessage(packs) {
  return (Array.isArray(packs) ? packs : []).map((pack) => ({
    type: normalizePackType(pack?.type),
    label: pack?.label || PACK_LABELS[normalizePackType(pack?.type)] || 'Pack',
    title: pack?.title || `${PACK_LABELS[normalizePackType(pack?.type)] || 'Pack'} Deco`,
    valor_final: pack?.valor_final ?? pack?.quote?.valor_final ?? null,
    currency: pack?.currency || pack?.quote?.currency || 'ARS',
    purchase_comparison: normalizePurchaseComparison(pack?.purchase_comparison),
  }));
}

function normalizePurchaseComparison(value) {
  if (!value || typeof value !== 'object') return null;
  const purchaseInstallment = Number(value.purchase_installment_12);
  const subscriptionMonthly = Number(value.subscription_monthly);
  const savingsMonthly = Number(value.savings_monthly);
  if (!Number.isFinite(purchaseInstallment) || !Number.isFinite(subscriptionMonthly)) return null;

  return {
    purchase_total: Number.isFinite(Number(value.purchase_total)) ? Number(value.purchase_total) : null,
    purchase_installment_12: purchaseInstallment,
    subscription_monthly: subscriptionMonthly,
    savings_monthly: Number.isFinite(savingsMonthly) ? savingsMonthly : Math.max(purchaseInstallment - subscriptionMonthly, 0),
    currency: value.currency || 'ARS',
  };
}

function renderPackComparisonSummary(comparison) {
  const normalized = normalizePurchaseComparison(comparison);
  if (!normalized) return '';

  return `
    <span class="deco-assistant__pack-chip-comparison">
      <span>Compra 12 cuotas: ${escapeHtml(formatCurrency(normalized.purchase_installment_12, normalized.currency))}/mes</span>
      <strong>Suscripción: ${escapeHtml(formatCurrency(normalized.subscription_monthly, normalized.currency))}/mes</strong>
      <em>Ahorrás ${escapeHtml(formatCurrency(normalized.savings_monthly, normalized.currency))}/mes</em>
    </span>
  `;
}

function normalizePackItems(items, products) {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      const product = findProductForItem(item, products);
      const variant = findVariantForItem(item, product);
      if (!product || !variant) {
        const directVariantId = normalizeId(item.variant_id ?? item.variantId ?? item.variant?.id ?? item.id);
        if (!directVariantId) return null;
        return {
          id: directVariantId,
          sku: item.sku || item.sku_shopify || '',
          qty: clamp(Number(item.qty || item.quantity || 1), 1, 20),
          title: item.title || item.name || item.nombre || item.product_title || item.sku || 'Producto Deco',
          productTitle: item.productTitle || item.product_title || item.title || item.name || item.nombre || item.sku || '',
          image_url: resolveItemImageUrl(item),
          puntaje: Number.isFinite(Number(item.puntaje)) ? Number(item.puntaje) : null,
          product_url: resolveItemProductUrl(item),
        };
      }

      return {
        id: variant.id,
        sku: variant.sku || item.sku || item.sku_shopify || '',
        qty: clamp(Number(item.qty || item.quantity || 1), 1, 20),
        title: item.title || item.name || item.nombre || item.product_title || product.title,
        productTitle: product.title,
        image_url: resolveItemImageUrl(item, product),
        puntaje: Number.isFinite(Number(item.puntaje)) ? Number(item.puntaje) : null,
        product_url: resolveItemProductUrl(item, product, variant),
      };
    })
    .filter(Boolean);
}

function renderPackItem(item) {
  const title = item.title || item.productTitle || item.sku || 'Producto Deco';
  const imageUrl = normalizeImageUrl(item.image_url);
  const productUrl = normalizeProductUrl(item.product_url);
  const placeholder = String(title).charAt(0).toUpperCase() || 'D';
  const imageMarkup = imageUrl
    ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(title)}" loading="lazy" width="58" height="58">`
    : `<span class="deco-assistant__pack-item-placeholder" aria-hidden="true">${escapeHtml(placeholder)}</span>`;

  return `
    <li class="deco-assistant__pack-item">
      <button
        class="deco-assistant__pack-item-preview"
        type="button"
        data-assistant-product-preview
        data-product-url="${escapeHtml(productUrl)}"
        data-sku="${escapeHtml(item.sku || '')}"
        data-name="${escapeHtml(title)}"
        aria-label="Ver detalle de ${escapeHtml(title)}"
      >
      <span
        class="deco-assistant__pack-item-photo"
        data-assistant-product-image
        data-sku="${escapeHtml(item.sku || '')}"
        data-name="${escapeHtml(title)}"
        data-image-loaded="${imageUrl ? 'true' : 'false'}"
      >${imageMarkup}</span>
      <span class="deco-assistant__pack-item-main">
        <span class="deco-assistant__pack-item-name">${escapeHtml(title)}</span>
        <span class="deco-assistant__pack-item-qty">Cantidad: ${escapeHtml(item.qty)}</span>
      </span>
      </button>
    </li>
  `;
}

function hydratePackItemImages(scope) {
  scope.querySelectorAll('[data-assistant-product-image]').forEach((container) => {
    if (!(container instanceof HTMLElement) || container.dataset.imageLoaded === 'true') return;

    const sku = container.dataset.sku || '';
    const name = container.dataset.name || '';
    resolvePackItemImage({ sku, title: name }).then((imageUrl) => {
      if (!imageUrl || !container.isConnected || container.dataset.imageLoaded === 'true') return;

      container.innerHTML = `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(name || sku)}" loading="lazy" width="58" height="58">`;
      container.dataset.imageLoaded = 'true';
    }).catch((_error) => {
      // Si Shopify no devuelve imagen, mantenemos el placeholder visual.
    });
  });
}

function findProductForItem(item, products) {
  const variantId = normalizeId(item.variant_id ?? item.variantId ?? item.variant?.id ?? item.id);
  const productId = normalizeId(item.product_id ?? item.productId ?? item.product?.id ?? item.id);
  const sku = normalizeText(item.sku || '');
  const title = normalizeText(item.title || item.name || '');

  if (variantId) {
    const byVariant = products.find((product) => product.variants?.some((variant) => normalizeId(variant.id) === variantId));
    if (byVariant) return byVariant;
  }

  if (productId) {
    const byProductId = products.find((product) => normalizeId(product.id) === productId);
    if (byProductId) return byProductId;
  }

  if (sku) {
    const bySku = products.find((product) => product.variants?.some((variant) => normalizeText(variant.sku || '') === sku));
    if (bySku) return bySku;
  }

  if (title) {
    return products.find((product) => normalizeText(product.title || '').includes(title) || title.includes(normalizeText(product.title || ''))) || null;
  }

  return null;
}

function findVariantForItem(item, product) {
  if (!product) return null;

  const variantId = normalizeId(item.variant_id ?? item.variantId ?? item.variant?.id ?? item.id);
  if (variantId) {
    const variant = product.variants?.find((candidate) => normalizeId(candidate.id) === variantId);
    if (variant) return variant;
  }

  const sku = normalizeText(item.sku || '');
  if (sku) {
    const variant = product.variants?.find((candidate) => normalizeText(candidate.sku || '') === sku);
    if (variant) return variant;
  }

  return product.firstAvailableVariant;
}

function readAssistantProducts(root) {
  const element = root.querySelector('[data-assistant-products]');
  if (!element) return [];

  try {
    const products = JSON.parse(element.textContent || '[]');
    if (!Array.isArray(products)) return [];

    return products.map((product) => {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      return {
        ...product,
        variants,
        firstAvailableVariant: variants.find((variant) => variant?.available) || variants[0] || null,
      };
    });
  } catch (_error) {
    return [];
  }
}

function resolveItemImageUrl(item, product = null) {
  const candidates = [
    item?.image_url,
    item?.imageUrl,
    item?.image,
    item?.image_src,
    item?.thumbnail,
    item?.featured_image,
    item?.featuredImage,
    item?.featured_media?.preview_image,
    item?.featured_media?.image,
    item?.images?.[0],
    item?.product?.image_url,
    item?.product?.image,
    item?.product?.featured_image,
    item?.variant?.image_url,
    item?.variant?.image,
    product?.image_url,
    product?.image,
    product?.featured_image,
    product?.featuredImage,
    product?.featured_media?.preview_image,
    product?.featured_media?.image,
  ];

  for (const candidate of candidates) {
    const imageUrl = normalizeImageUrl(candidate);
    if (imageUrl) return imageUrl;
  }
  return '';
}

function resolveItemProductUrl(item, product = null, variant = null) {
  const candidates = [
    item?.product_url,
    item?.productUrl,
    item?.url,
    item?.href,
    item?.online_store_url,
    item?.product?.url,
    item?.product?.product_url,
    product?.url,
    product?.product_url,
    product?.handle ? `/products/${product.handle}` : '',
    item?.handle ? `/products/${item.handle}` : '',
    item?.product?.handle ? `/products/${item.product.handle}` : '',
  ];

  for (const candidate of candidates) {
    const productUrl = normalizeProductUrl(candidate);
    if (!productUrl) continue;

    if (variant?.id && !productUrl.includes('variant=')) {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);
      return url.href;
    }
    return productUrl;
  }
  return '';
}

function normalizeProductUrl(value) {
  if (!value) return '';

  try {
    const url = new URL(String(value), window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return '';
    return url.href;
  } catch (_error) {
    return '';
  }
}

function normalizeSku(value) {
  return String(value || '').trim().toUpperCase();
}

function normalizeImageUrl(value) {
  const rawValue = typeof value === 'string' ? value : (value?.url || value?.src || value?.original_src || '');
  if (!rawValue) return '';

  try {
    return new URL(rawValue, window.location.origin).href;
  } catch (_error) {
    return '';
  }
}

function readProductImageCache() {
  try {
    const cached = JSON.parse(sessionStorage.getItem(PRODUCT_IMAGE_CACHE_STORAGE_KEY) || '{}');
    return cached && typeof cached === 'object' ? cached : {};
  } catch (_error) {
    return {};
  }
}

function writeProductImageCache(cache) {
  try {
    sessionStorage.setItem(PRODUCT_IMAGE_CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch (_error) {
    // ignore cache write errors
  }
}

function productImageCacheKeys(item) {
  const keys = [];
  const sku = normalizeSku(item?.sku);
  const name = normalizeText(item?.title || item?.name || item?.nombre || item?.productTitle || item?.product_title || '');
  if (sku) keys.push(sku);
  if (name) keys.push(`name:${name}`);
  return keys;
}

function getCachedProductImage(item) {
  const keys = productImageCacheKeys(item);
  if (!keys.length) return '';

  const cache = readProductImageCache();
  for (const key of keys) {
    const entry = cache[key];
    if (!entry) continue;

    if (Date.now() - Number(entry.updatedAt || 0) > PRODUCT_IMAGE_CACHE_TTL_MS) {
      delete cache[key];
      writeProductImageCache(cache);
      continue;
    }
    return normalizeImageUrl(entry.url);
  }
  return '';
}

function setCachedProductImage(item, url) {
  const imageUrl = normalizeImageUrl(url);
  if (!imageUrl) return;

  const keys = productImageCacheKeys(item);
  if (!keys.length) return;

  const cache = readProductImageCache();
  keys.forEach((key) => {
    cache[key] = {
      url: imageUrl,
      updatedAt: Date.now(),
    };
  });
  writeProductImageCache(cache);
}

function findProductImageInDom(sku) {
  const normalizedSku = normalizeSku(sku);
  if (!normalizedSku) return '';

  const card = Array.from(document.querySelectorAll('product-card[data-product-sku]'))
    .find((element) => normalizeSku(element.dataset.productSku) === normalizedSku);
  if (!(card instanceof HTMLElement)) return '';

  const featuredMediaUrl = card.closest('product-card-link')?.dataset?.featuredMediaUrl || '';
  const fromFeaturedMedia = normalizeImageUrl(featuredMediaUrl);
  if (fromFeaturedMedia) return fromFeaturedMedia;

  const image = card.querySelector('img');
  return normalizeImageUrl(image?.currentSrc || image?.src || '');
}

function getPredictiveProductImage(product) {
  const candidates = [
    product?.image,
    product?.featured_image,
    product?.featuredImage,
    product?.featured_media?.preview_image,
    product?.featured_media?.image,
    product?.images?.[0],
  ];

  for (const candidate of candidates) {
    const imageUrl = normalizeImageUrl(candidate);
    if (imageUrl) return imageUrl;
  }
  return '';
}

async function fetchProductImageFromSearch(query) {
  const cleanQuery = String(query || '').trim();
  if (!cleanQuery) return '';

  const url = new URL('/search/suggest.json', window.location.origin);
  url.searchParams.set('q', cleanQuery);
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('resources[limit]', '5');
  url.searchParams.set('resources[options][unavailable_products]', 'show');

  const response = await fetchWithTimeout(url.toString(), {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return '';

  const data = await response.json();
  const products = data?.resources?.results?.products || [];
  if (!Array.isArray(products)) return '';

  for (const product of products) {
    const imageUrl = getPredictiveProductImage(product);
    if (imageUrl) return imageUrl;
  }
  return '';
}

function getPredictiveProductUrl(product) {
  return normalizeProductUrl(product?.url || product?.handle && `/products/${product.handle}`);
}

async function fetchProductUrlFromSearch(query) {
  const cleanQuery = String(query || '').trim();
  if (!cleanQuery) return '';

  const url = new URL('/search/suggest.json', window.location.origin);
  url.searchParams.set('q', cleanQuery);
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('resources[limit]', '5');
  url.searchParams.set('resources[options][unavailable_products]', 'show');

  const response = await fetchWithTimeout(url.toString(), {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return '';

  const data = await response.json();
  const products = data?.resources?.results?.products || [];
  if (!Array.isArray(products)) return '';

  for (const product of products) {
    const productUrl = getPredictiveProductUrl(product);
    if (productUrl) return productUrl;
  }
  return '';
}

async function resolvePackItemProductUrl(item) {
  const explicitUrl = normalizeProductUrl(item?.product_url);
  if (explicitUrl) return explicitUrl;

  const sku = normalizeSku(item?.sku);
  const bySku = sku ? await fetchProductUrlFromSearch(sku) : '';
  if (bySku) return bySku;

  return fetchProductUrlFromSearch(item?.title || item?.name || item?.nombre || item?.productTitle || '');
}

async function resolvePackItemImage(item) {
  const explicitImage = normalizeImageUrl(item?.image_url);
  if (explicitImage) return explicitImage;

  const sku = normalizeSku(item?.sku);
  if (sku) {
    const domImage = findProductImageInDom(sku);
    if (domImage) {
      setCachedProductImage(item, domImage);
      return domImage;
    }
  }

  const cachedImage = getCachedProductImage(item);
  if (cachedImage) return cachedImage;

  const requestKey = productImageCacheKeys(item)[0];
  if (!requestKey) return '';

  if (productImageRequests.has(requestKey)) {
    return productImageRequests.get(requestKey);
  }

  const request = (async () => {
    const bySku = sku ? await fetchProductImageFromSearch(sku) : '';
    const imageUrl = bySku || await fetchProductImageFromSearch(item?.title || item?.name || item?.nombre || item?.productTitle || '');
    setCachedProductImage(item, imageUrl);
    return imageUrl;
  })().finally(() => {
    productImageRequests.delete(requestKey);
  });

  productImageRequests.set(requestKey, request);
  return request;
}

async function addPackToCart(root, items) {
  const lineItems = items.map((item, index) => ({
    sku: item.sku,
    qty: item.qty,
    order: index + 1,
    source: 'ASISTENTE',
  }));
  if (lineItems.some((item) => !String(item.sku || '').trim())) {
    throw new Error('Uno de los productos recomendados no tiene SKU.');
  }

  const state = await addSystemSelectionItems(lineItems, { source: 'ASISTENTE' });

  document.dispatchEvent(
    new CartAddEvent(state, String(lineItems[0]?.sku || root.id || 'deco-pack-assistant'), {
      source: 'deco-pack-assistant',
      action: 'add',
      itemCount: Number(state.item_count || 0),
      itemCountIsTotal: true,
      variantId: String(lineItems[0]?.sku || ''),
    })
  );
  openCartDrawerIfAvailable();
  return state;
}

function openCartDrawerIfAvailable() {
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (!(cartDrawer instanceof HTMLElement)) return false;

  if (typeof cartDrawer.open === 'function') {
    cartDrawer.open();
    return true;
  }

  if (typeof cartDrawer.showDialog === 'function') {
    cartDrawer.showDialog();
    return true;
  }

  return false;
}

function formatCurrency(value, currency = 'ARS') {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency || 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function getVisitorId() {
  try {
    const existing = localStorage.getItem(VISITOR_STORAGE_KEY);
    if (existing) return existing;

    const generated = `visitor_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(VISITOR_STORAGE_KEY, generated);
    return generated;
  } catch (_error) {
    return '';
  }
}

function normalizePackType(value) {
  const text = normalizeText(value);
  if (text.includes('premium')) return 'premium';
  if (text.includes('recomend') || text.includes('equilibr')) return 'recomendado';
  if (text.includes('econom') || text.includes('ahorro') || text.includes('basico')) return 'economico';
  return PACK_ORDER.includes(text) ? text : 'recomendado';
}

function readSavedState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch (_error) {
    return {};
  }
}

function normalizeBudgetNotice(value) {
  if (!value || typeof value !== 'object') return null;
  const message = String(value.message || '').trim();
  return {
    ...value,
    ...(message ? { message } : {}),
  };
}

function saveState(state) {
  const value = JSON.stringify({
    conversationState: state.conversationState || {},
    messages: state.messages || [],
    requestId: state.requestId || '',
    recommendations: state.recommendations || [],
    budgetNotice: state.budgetNotice || null,
    updatedAt: new Date().toISOString(),
  });

  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch (_error) {
    try {
      sessionStorage.setItem(STORAGE_KEY, value);
    } catch (_secondError) {
      // El almacenamiento local puede no estar disponible en algunos navegadores.
    }
  }
}

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function normalizeId(value) {
  const text = String(value ?? '').trim();
  const match = text.match(/(\d+)$/);
  return match ? match[1] : text;
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function clamp(value, min, max) {
  const numeric = Number.isFinite(value) ? value : min;
  return Math.min(max, Math.max(min, numeric));
}
